import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, Grid, TextField, Button, InputLabel, FormControl, MenuItem, Select, Typography, CircularProgress } from '@mui/material';
import { makeApiRequest } from '../../../utils/api';

const AddLearningJourney = () => {
    const [moduleList, setModuleList] = useState([]);
    const [chapterList, setChapterList] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const today = new Date();
    const yesterday = new Date(today);
    const [filters, setFilters] = useState({ module: '', chapter: '' });
    yesterday.setDate(today.getDate() - 1);

    const [formData, setFormData] = useState({
        date: today.toISOString().split('T')[0],
        name: '',
        inTime: '',
        outTime: '',
        description: '',
        control: 3,
        module: '',
        chapter: '',
        user_id: localStorage.getItem('id')
    });

    useEffect(() => {
        // Fetch module list once when component mounts
        fetchModuleData();
    }, []);
    
    useEffect(() => {
        // Fetch chapter list whenever selected module changes
        if (filters.module) {
            fetchChapterData();
        } else {
            setChapterList([]); // Reset chapter list when module is unselected
        }
    }, [filters.module]);
    

    const fetchModuleData = async () => {
        try {
            const response = await makeApiRequest('post', '/api/module/list', {
                token: localStorage.getItem('authToken'),
                per_page: 100
            });
            if (response.status === 'success' && response.data) {
                setModuleList(response.data.data);
            } else {
                throw new Error("Failed to fetch modules");
            }
        } catch (error) {
            console.error("Error fetching modules:", error);
            toast.error("Failed to load modules.");
        }
    };

    const fetchChapterData = async () => {
        try {
            const response = await makeApiRequest('post', '/api/chapter/listbyModule', {
                token: localStorage.getItem('authToken'),
                id: filters.module
            });
            if (response.status === 'success' && response.data) {
                setChapterList(response.data);
            }
        } catch (error) {
            console.error("Error fetching chapter:", error);
            toast.error("Failed to load chapters.");
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
        setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    };

    const validateTimes = () => {
        if (formData.inTime && formData.outTime && formData.inTime >= formData.outTime) {
            return 'Out time must be after in time';
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const timeError = validateTimes();
        if (timeError) {
            setError(timeError);
            setLoading(false);
            return;
        }

        if (!filters.module || !filters.chapter || !formData.inTime || !formData.outTime || !formData.description) {
            toast.warn("Please fill all the required fields");
            setLoading(false);
            return;
        }

        try {
            const response = await makeApiRequest('post', '/api/learning/add', {
                token: localStorage.getItem('authToken'),
                data: { ...formData, module: filters.module, chapter: filters.chapter }
            });

            if (response.status === 'success') {
                toast.success(response.message);
                // window.location.reload(false);
            } else {
                throw new Error('Invalid attendance data received');
            }
        } catch (err) {
            console.error('Error submitting data:', err);
            setError(err.response?.data?.message || 'Error submitting learning journey data');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ height: 'auto', backgroundColor: '#f1f4f9', minHeight: '100vh' }}>
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover={false}
                theme="light"
            />
            <Grid container sx={{ height: "auto", width: "100%", display: "flex", justifyContent: "center", }}>

                <Grid item xs={11} sx={{ height: "80px", width: "100%", display: "flex", alignItems: "center" }}>

                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontSize: { xs: '24px', sm: "34px" },
                            fontStyle: 'normal',
                            fontWeight: '500',
                            lineHeight: '160%',
                            letterSpacing: "0.15px",
                            color: '#481E82',
                            alignSelf: 'flex-start',
                            // paddingLeft: '15px',
                            height: "100%",
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        Add Learning Journey
                    </Typography>
                </Grid>
                <Grid item xs={11}>
                    <Box sx={{ maxWidth: "100%", mx: 'auto', p: 3, borderRadius: 2, boxShadow: 3, backgroundColor: 'white' }}>
                        {loading && <CircularProgress />}
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        size='small'
                                        sx={{
                                            width: "100%",
                                            '& .MuiFormLabel-asterisk': {
                                                color: 'red',
                                            },
                                        }}
                                        required
                                        label=" Task Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth
                                        sx={{ maxWidth: "100%" }}
                                        size="small">
                                        <InputLabel id="date-label">Date</InputLabel>
                                        <Select
                                            labelId="date-label"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            variant="outlined"
                                            label="Date"
                                        >
                                            <MenuItem value={today.toISOString().split('T')[0]}>Today</MenuItem>
                                            <MenuItem value={yesterday.toISOString().split('T')[0]}>Yesterday</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="module-label">Module</InputLabel>
                                        <Select
                                            labelId="module-label"
                                            name="module"
                                            value={filters.module}
                                            // onClick={fetchModuleData}
                                            onChange={handleChange}
                                            variant="outlined"
                                            label="Module">
                                            {moduleList && moduleList.map((module) => (
                                                <MenuItem key={module.module_id} value={module.module_id}>
                                                    {module.module}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="chapter-label">Chapter</InputLabel>
                                        <Select
                                            labelId="chapter-label"
                                            name="chapter"
                                            value={filters.chapter}
                                            // onClick={fetchChapterData}
                                            onChange={handleChange}
                                            variant="outlined"
                                            label="Chapter">
                                            {chapterList && chapterList.map((chapter) => (
                                                <MenuItem key={chapter.chapter_id} value={chapter.chapter_id}>
                                                    {chapter.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        size='small'
                                        sx={{
                                            width: "100%",
                                            '& .MuiFormLabel-asterisk': {
                                                color: 'red',
                                            },
                                        }}
                                        required
                                        type="time"
                                        label="In Time"
                                        name="inTime"
                                        value={formData.inTime}
                                        onChange={handleChange}
                                        onFocus={(e) => e.target.showPicker()}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        size='small'
                                        sx={{
                                            width: "100%",
                                            '& .MuiFormLabel-asterisk': {
                                                color: 'red',
                                            },
                                        }}
                                        required
                                        type="time"
                                        label="Out Time"
                                        name="outTime"
                                        value={formData.outTime}
                                        onChange={handleChange}
                                        onFocus={(e) => e.target.showPicker()}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        required
                                        sx={{
                                            '& .MuiFormLabel-asterisk': {
                                                color: 'red',
                                            },
                                        }}
                                        label="Description (Max 250 Chars)"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        inputProps={{ maxLength: 250 }}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>

                            {error && (
                                <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                                    {error}
                                </Typography>
                            )}

                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <Button variant="contained" type="submit" color="primary" disabled={loading || !formData.inTime || !formData.outTime}>
                                    ADD LEARNING JOURNEY
                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddLearningJourney;
